import { isPlatformBrowser } from '@angular/common';
import { computed, inject, PLATFORM_ID } from '@angular/core';

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';

/**
 * State interface for NavigationSidebarStore.
 * Uses serializable types (string[] instead of Set) for devtools compatibility.
 */
interface NavigationSidebarState {
  /** Whether sidebar is collapsed (icons only) */
  collapsed: boolean;
  /** Whether sidebar is in hover-to-peek mode (temporarily expanded) */
  peeking: boolean;
  /** Array of entry IDs that are expanded (sections or root groups) */
  expandedEntryIds: string[];
  /** Whether viewport is at or above the lg breakpoint */
  isLargeScreen: boolean;
}

const STORAGE_KEY_SM = 'nav-collapsed-sm';
const STORAGE_KEY_LG = 'nav-collapsed-lg';
const BREAKPOINT_QUERY = '(min-width: 1024px)';

const initialState: NavigationSidebarState = {
  collapsed: false,
  peeking: false,
  expandedEntryIds: [],
  isLargeScreen: true,
};

/**
 * Load collapsed preference from localStorage for the given breakpoint.
 * Default: sm → collapsed, lg → expanded. Safe on SSR (returns default).
 */
function loadCollapsedPreference(isLargeScreen: boolean, isBrowser: boolean): boolean {
  if (!isBrowser) return !isLargeScreen;
  try {
    const key = isLargeScreen ? STORAGE_KEY_LG : STORAGE_KEY_SM;
    const stored = localStorage.getItem(key);
    if (stored !== null) return stored === 'true';
    return !isLargeScreen;
  } catch {
    return !isLargeScreen;
  }
}

/**
 * Save collapsed preference for the given breakpoint. Safe on SSR (no-op).
 */
function saveCollapsedPreference(collapsed: boolean, isLargeScreen: boolean, isBrowser: boolean): void {
  if (!isBrowser) return;
  try {
    const key = isLargeScreen ? STORAGE_KEY_LG : STORAGE_KEY_SM;
    localStorage.setItem(key, String(collapsed));
  } catch {
    // localStorage unavailable (private mode) — ignore
  }
}

/**
 * NavigationSidebarStore - Global navigation state management.
 *
 * NOTE: Unlike route-scoped stores (FilterStore, ViewStateStore),
 * this store uses providedIn: 'root' because navigation state
 * must persist across all route navigations.
 *
 * Manages:
 * - Collapsed/expanded sidebar state (with localStorage persistence per breakpoint)
 * - Entry expansion states (sections and root groups)
 * - Hover-to-peek transient state
 * - Breakpoint awareness (lg = 1024px)
 *
 * @example
 * ```typescript
 * const store = inject(NavigationSidebarStore);
 *
 * // Toggle sidebar
 * store.toggleCollapsed();
 *
 * // Toggle section/group
 * store.toggleSection('catalog');
 *
 * // Check if section/group is expanded
 * if (store.isSectionExpanded('catalog')) { ... }
 * ```
 */
export const NavigationSidebarStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed(({ collapsed, peeking, expandedEntryIds }) => ({
    /** CSS width value based on collapsed state (layout width, ignores peeking) */
    width: computed(() => (collapsed() ? '4rem' : '14rem')),
    /** Visual collapsed state: collapsed unless peeking */
    effectiveCollapsed: computed(() => collapsed() && !peeking()),
    /** O(1) lookup set of expanded entry ids */
    expandedEntryIdSet: computed(() => new Set(expandedEntryIds())),
  })),

  withMethods((store) => {
    const platformId = inject(PLATFORM_ID);
    const isBrowser = isPlatformBrowser(platformId);

    return {
      /** Toggle collapsed state and persist the new value. */
      toggleCollapsed(): void {
        const next = !store.collapsed();
        patchState(store, { collapsed: next });
        saveCollapsedPreference(next, store.isLargeScreen(), isBrowser);
      },

      /** Set collapsed state explicitly (no persistence — used for init/breakpoint sync). */
      setCollapsed(collapsed: boolean): void {
        patchState(store, { collapsed });
      },

      /** Set peeking state (hover-to-peek) */
      setPeeking(peeking: boolean): void {
        patchState(store, { peeking });
      },

      /** Record large-screen state and re-sync collapsed from stored preference. */
      setLargeScreen(isLargeScreen: boolean): void {
        patchState(store, { isLargeScreen });
        patchState(store, {
          collapsed: loadCollapsedPreference(isLargeScreen, isBrowser),
          peeking: false,
        });
      },

      /** Check if a section/group is expanded (O(1) via computed set). */
      isSectionExpanded(sectionId: string): boolean {
        return store.expandedEntryIdSet().has(sectionId);
      },

      /** Toggle a section/group's expanded state */
      toggleSection(sectionId: string): void {
        const current = store.expandedEntryIds();
        const isExpanded = store.expandedEntryIdSet().has(sectionId);
        patchState(store, {
          expandedEntryIds: isExpanded
            ? current.filter((id) => id !== sectionId)
            : [...current, sectionId],
        });
      },

      /** Expand a section/group (used for auto-expand on route match) */
      expandSection(sectionId: string): void {
        if (store.expandedEntryIdSet().has(sectionId)) return;
        patchState(store, {
          expandedEntryIds: [...store.expandedEntryIds(), sectionId],
        });
      },

      /** Collapse a section/group */
      collapseSection(sectionId: string): void {
        if (!store.expandedEntryIdSet().has(sectionId)) return;
        patchState(store, {
          expandedEntryIds: store.expandedEntryIds().filter((id) => id !== sectionId),
        });
      },

      /** Reset store to initial state */
      reset(): void {
        patchState(store, initialState);
      },
    };
  }),

  withHooks({
    onInit(store) {
      // Initialise breakpoint + persisted collapsed preference on creation.
      const platformId = inject(PLATFORM_ID);
      if (!isPlatformBrowser(platformId)) return;

      const mq = window.matchMedia(BREAKPOINT_QUERY);
      store.setLargeScreen(mq.matches);

      const onChange = (e: MediaQueryListEvent): void => {
        store.setLargeScreen(e.matches);
      };
      mq.addEventListener('change', onChange);
    },
  })
);

/** Type export for DI typing */
export type NavigationSidebarStore = InstanceType<typeof NavigationSidebarStore>;
